/**
 * @file Permanent Recall edit modal.
 */

import { appData } from "../../app/state/state.js";
import { closeForm } from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
    bindNativeFieldBehavior,
    getNativeFieldWrapperHtml,
    NATIVE_INPUT_CLASSES,
    validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { updateRecallDetails } from "./recallsApi.js";
import {
    collectRecallEntryData,
    confirmNearDateIfNeeded,
    handleRecallWarningResponse,
} from "./recallsEntry.js";
import {
    RECALLS_MODAL_BODY_SPACING_CLASSES,
    RECALLS_MODAL_GRID_CLASSES,
    setRecallsModalActionProcessing,
} from "./recallsModalLayout.js";
import {
    shouldConfirmNearDateForEdit,
} from "./recallsEditGuards.js";
import {
    normalizeRecallReasonIds,
    serializeRecallReasonIds,
} from "./recallsReasonUtils.js";
import {
    bindRecallPhoneInputSanitizer,
} from "./recallsPhoneValidation.js";
import {
    createRecallProviderSelectPreset,
    getRecallProviderControlValues,
    parseRecallProviderMirrorValue,
} from "./recallsProviderUtils.js";
import {
    openRecallsConfigIncompleteModal,
} from "./recallsConfigIncomplete.js";
import {
    applyRecallDetailsMutationToRouteCaches,
    findCachedRecall,
} from "./recallsState.js";

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function getReasonOptions() {
    return (appData.config?.recalls?.recallReasons || [])
        .filter((option) => option?.id && option?.isActive !== false)
        .map((option) => ({
            value: option.id,
            label: option.name || option.id,
            source: option,
        }));
}

function getTextInputHtml({
    name,
    placeholder,
    required = false,
    type = "text",
    value = "",
}) {
    return `
        <input
            type="${attrValue(type)}"
            name="${attrValue(name)}"
            value="${attrValue(value)}"
            placeholder="${attrValue(placeholder)}"
            class="${NATIVE_INPUT_CLASSES}"
            data-recalls-entry-input
            ${required ? "required" : ""}
        >
    `;
}

function getReasonSelectHtml(item = {}) {
    return `
        <div data-recalls-edit-reason-control></div>
        <input type="hidden" name="recall_reason" value="${attrValue(serializeRecallReasonIds(item.recall_reason))}" data-recalls-edit-reason-mirror data-recalls-entry-input>
    `;
}

function getProviderSelectHtml(item = {}) {
    const selectedIds = getRecallProviderControlValues(item.provider);
    return `
        <div data-recalls-edit-provider-control></div>
        <input type="hidden" name="provider" value="${attrValue(JSON.stringify(selectedIds))}" data-recalls-edit-provider-mirror data-recalls-entry-input>
    `;
}

function getDateControlHtml(item = {}) {
    return `
        <div data-recalls-edit-date-control></div>
        <input type="hidden" name="recall_date" value="${attrValue(item.recall_date)}" data-recalls-edit-date-mirror data-recalls-entry-input>
    `;
}

function getEditFieldsHtml(item = {}) {
    return `
        <div class="${RECALLS_MODAL_GRID_CLASSES} ${RECALLS_MODAL_BODY_SPACING_CLASSES}" data-recalls-edit-fields>
            ${getNativeFieldWrapperHtml("First Name", getTextInputHtml({
                name: "first_name",
                placeholder: "First name",
                required: true,
                value: item.first_name,
            }), { required: true })}
            ${getNativeFieldWrapperHtml("Last Name", getTextInputHtml({
                name: "last_name",
                placeholder: "Last name",
                required: true,
                value: item.last_name,
            }), { required: true })}
            ${getNativeFieldWrapperHtml("Email", getTextInputHtml({
                name: "email",
                placeholder: "patient@example.com",
                value: item.email,
            }))}
            ${getNativeFieldWrapperHtml("Phone", getTextInputHtml({
                name: "phone",
                placeholder: "Phone",
                type: "tel",
                value: item.phone,
            }))}
            ${getNativeFieldWrapperHtml("Reason", getReasonSelectHtml(item), {
                required: true,
            })}
            ${getNativeFieldWrapperHtml("Provider", getProviderSelectHtml(item), {
                required: true,
            })}
            ${getNativeFieldWrapperHtml("Recall Date", getDateControlHtml(item), {
                required: true,
            })}
        </div>
    `;
}

function mountReasonControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-edit-reason-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-edit-reason-mirror]");
    if (!root || !mirrorInput) {return;}

    createSelectControl({
        root,
        mirrorInput,
        name: "recall_reason",
        value: normalizeRecallReasonIds(mirrorInput.value),
        options: getReasonOptions(),
        placeholder: "+ Add reason",
        multiple: true,
        clearable: true,
        portalMenu: true,
        required: true,
        requiredMessage: "Recall reason is required.",
    });
}

function mountProviderControl(itemForm, item = {}) {
    const root = itemForm.querySelector("[data-recalls-edit-provider-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-edit-provider-mirror]");
    if (!root || !mirrorInput) {return;}

    createRecallProviderSelectPreset({
        root,
        appData,
        mirrorInput,
        name: "provider",
        selectedProviders: item.provider || [],
        value: parseRecallProviderMirrorValue(mirrorInput.value),
        searchable: true,
        portalMenu: true,
        required: true,
        requiredMessage: "Provider is required.",
    });
}

function mountDateControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-edit-date-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-edit-date-mirror]");
    if (!root || !mirrorInput) {return;}

    createDateControl({
        root,
        mirrorInput,
        name: "recall_date",
        required: true,
        requiredMessage: "Recall date is required.",
        type: "date",
        value: mirrorInput.value || "",
    });
}

function getCallableErrorMessage(error) {
    return error?.message || "The Recall details could not be saved.";
}

function showErrorModal(title, message) {
    openConfirmationModal(
        title,
        escapeHTML(message),
        async () => {},
        false,
        "OK",
    );
}

async function saveRecallEdit({ data, item, onSuccess }) {
    const action = async (extra = {}) => updateRecallDetails({
        recallId: item.id,
        data,
        expectedUpdatedAt: item.updatedAt || null,
        ...extra,
    });
    const response = await action();
    await handleRecallWarningResponse({
        action,
        contactConfirmLabel: "Save anyway",
        data,
        duplicateConfirmLabel: "Save anyway",
        onSuccess: (result) => {
            applyRecallDetailsMutationToRouteCaches(item.id, data);
            closeForm();
            onSuccess?.(result);
        },
        response,
    });
}

/**
 * Opens the permanent Recall edit modal.
 * @param {object} item Recall row.
 * @param {{ onSuccess?: Function }} [options={}] Modal options.
 * @returns {void}
 */
export function openRecallEditModal(item, options = {}) {
    if (!item?.id || item.isClosedResolved === true) {return;}

    openStandardForm("recall_details", item, {
        getFieldsHtml: getEditFieldsHtml,
        afterRender({ itemForm }) {
            const submitBtn = itemForm.querySelector("button[type=\"submit\"]");
            if (submitBtn) {submitBtn.textContent = "Save";}
            bindNativeFieldBehavior(itemForm);
            bindRecallPhoneInputSanitizer(itemForm);
            mountReasonControl(itemForm);
            mountProviderControl(itemForm, item);
            mountDateControl(itemForm);
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const fieldsRoot = itemForm.querySelector("[data-recalls-edit-fields]");
                if (!fieldsRoot) {return;}
                fieldsRoot.querySelector('[name="phone"]')?.setCustomValidity("");
                const nativeValid = validateNativeFormFields(itemForm);
                const registeredValidation = validateRegisteredControls(itemForm);
                if (!nativeValid || !registeredValidation.valid) {
                    if (nativeValid) {
                        focusFirstRegisteredControlError(registeredValidation);
                    }
                    return;
                }
                const data = collectRecallEntryData(fieldsRoot);
                try {
                    setRecallsModalActionProcessing(submitBtn, true);
                    const saveAction = async () => {
                        await saveRecallEdit({
                            data,
                            item,
                            onSuccess: options.onSuccess,
                        });
                    };
                    if (shouldConfirmNearDateForEdit(item, data)) {
                        await confirmNearDateIfNeeded(data, saveAction);
                    } else {
                        await saveAction();
                    }
                } catch (error) {
                    if (openRecallsConfigIncompleteModal(error)) {return;}
                    showErrorModal("Save Recall Details", getCallableErrorMessage(error));
                } finally {
                    setRecallsModalActionProcessing(submitBtn, false, {
                        idleLabel: "Save",
                    });
                }
            };
        },
    });
}

/**
 * Binds permanent Recall edit buttons in the route.
 * @param {HTMLElement} container Route container.
 * @param {Function} rerender Route rerender callback.
 * @returns {void}
 */
export function bindRecallsEditActions(container, rerender) {
    container.querySelectorAll("[data-recalls-edit-details]")
        .forEach((button) => {
            button.addEventListener("click", () => {
                const recallId = button.dataset.recallsEditDetails;
                const item = findCachedRecall(recallId);
                if (!item) {return;}
                openRecallEditModal(item, { onSuccess: rerender });
            });
        });
}
